import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fetchAdminData, updateLeadStatus, addProject, updateProject, deleteProject, addCategory, deleteCategory, addMetric, addMetricData, deleteMetric, deleteMetricData, uploadChartImage, getCalendlyLink as fetchCalendlyLink } from '../services/adminApi';

const Admin: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [calendlyLink, setCalendlyLink] = useState('');
  const [newCalendlyLink, setNewCalendlyLink] = useState('');
  const [showCalendlyForm, setShowCalendlyForm] = useState(false);
  const [showAddProjectForm, setShowAddProjectForm] = useState(false);
  const [showAddCategoryForm, setShowAddCategoryForm] = useState(false);
  const [editingProject, setEditingProject] = useState<any>(null);
  const [selectedProjectMetrics, setSelectedProjectMetrics] = useState<any>(null);
  const [showAddMetricForm, setShowAddMetricForm] = useState(false);
  const [showAddMetricDataForm, setShowAddMetricDataForm] = useState(false);
  
  // Form states
  const [newProject, setNewProject] = useState({
    title: '',
    youtube_url: '',
    short_description: '',
    full_description: '',
    price_start: 0,
    roi_summary: '',
    time_to_implement: '',
    category_id: 1,
    image_url: ''
  });

  const [newCategory, setNewCategory] = useState({
    name: ''
  });

  const [newMetric, setNewMetric] = useState({
    metric_name: '',
    chart_type: 'line',
    x_label: '',
    y_label: '',
    chart_image_url: '',
    chart_description: '',
    chart_file: null as File | null
  });

  const [newMetricData, setNewMetricData] = useState({
    x_value: '',
    y_value: ''
  });

  useEffect(() => {
    if (authenticated) {
      loadAdminData();
    }
  }, [authenticated]);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const response = await fetchAdminData();
      
      // Update the state assignments to match the new response structure
      setProjects(response.data.data.projects);
      setLeads(response.data.data.leads);
      setCategories(response.data.data.categories);
      setStats(response.data.data.stats);
      
      // Extract and set the Calendly link
      if (response.data.data.calendlyLink) {
        setCalendlyLink(response.data.data.calendlyLink);
        setNewCalendlyLink(response.data.data.calendlyLink);
      }
    } catch (error: any) {
      console.error('Error fetching admin data:', error);
      alert('Failed to load admin data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Send the password to the backend for verification
    // In a real application, this would be an API call to validate credentials
    // For this implementation, we'll send the password to backend for validation
    fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api'}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Store the token received from the backend
        localStorage.setItem('adminToken', data.token || 'admin-secret-token');
        setAuthenticated(true);
      } else {
        alert('Invalid password');
      }
    })
    .catch(error => {
      console.error('Login error:', error);
      alert('Login failed: ' + error.message);
    });
  };

  // 添加登出功能
  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAuthenticated(false);
    setPassword('');
  };

  const toggleLeadStatus = async (leadId: number, currentStatus: boolean) => {
    try {
      await updateLeadStatus(leadId, !currentStatus);
      
      // Update local state
      setLeads((prev: any[]) => 
        prev.map((lead: any) => 
          lead.id === leadId ? { ...lead, processed: !currentStatus } : lead
        )
      );
    } catch (error: any) {
      console.error('Error updating lead status:', error);
      alert('Failed to update lead status: ' + error.message);
    }
  };

  const handleUpdateCalendlyLink = async () => {
    try {
      // Here you would typically call an API to update the Calendly link
      // For now, just close the modal and update the local state
      setCalendlyLink(newCalendlyLink);
      setShowCalendlyForm(false);
    } catch (error: any) {
      console.error('Error updating Calendly link:', error);
      alert('Failed to update Calendly link: ' + error.message);
    }
  };

  const handleAddProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addProject(newProject);
      loadAdminData(); // Refresh data
      setShowAddProjectForm(false);
      setNewProject({
        title: '',
        youtube_url: '',
        short_description: '',
        full_description: '',
        price_start: 0,
        roi_summary: '',
        time_to_implement: '',
        category_id: 1,
        image_url: ''
      });
    } catch (error: any) {
      console.error('Error adding project:', error);
      alert('Failed to add project: ' + error.message);
    }
  };

  const handleUpdateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateProject(editingProject.id, editingProject);
      loadAdminData(); // Refresh data
      setEditingProject(null);
    } catch (error: any) {
      console.error('Error updating project:', error);
      alert('Failed to update project: ' + error.message);
    }
  };

  const handleDeleteProject = async (projectId: number) => {
    if (window.confirm('Are you sure you want to delete this project?')) {
      try {
        await deleteProject(projectId);
        loadAdminData(); // Refresh data
      } catch (error: any) {
        console.error('Error deleting project:', error);
        alert('Failed to delete project: ' + error.message);
      }
    }
  };

  const handleDeleteMetric = async (metricId: number) => {
    try {
      await deleteMetric(metricId);
      loadAdminData(); // Refresh data
    } catch (error: any) {
      console.error('Error deleting metric:', error);
      alert('Failed to delete metric: ' + error.message);
    }
  };

  const handleAddCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addCategory(newCategory);
      loadAdminData(); // Refresh data
      setShowAddCategoryForm(false);
      setNewCategory({ name: '' });
    } catch (error: any) {
      console.error('Error adding category:', error);
      alert('Failed to add category: ' + error.message);
    }
  };

  const handleDeleteCategory = async (categoryId: number) => {
    if (window.confirm('Are you sure you want to delete this category? This will also delete all projects in this category.')) {
      try {
        await deleteCategory(categoryId);
        loadAdminData(); // Refresh data
      } catch (error: any) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category: ' + error.message);
      }
    }
  };

  // Handle file selection for chart upload
  const handleChartFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setNewMetric(prevState => ({
        ...prevState,
        chart_file: file
      }));
      
      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewMetric(prevState => ({
          ...prevState,
          chart_image_url: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddMetric = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectMetrics) return;
    
    try {
      let finalImageUrl = newMetric.chart_image_url;
      
      // If a file was selected, upload it to the server
      if (newMetric.chart_file) {
        finalImageUrl = await uploadChartImage(newMetric.chart_file);
      }
      
      // Prepare the metric data
      const metricData = newMetric.chart_type === 'custom' && finalImageUrl
        ? {
            metric_name: newMetric.metric_name,
            chart_type: 'custom',
            x_label: newMetric.x_label,
            y_label: newMetric.y_label,
            chart_image_url: finalImageUrl,
            chart_description: newMetric.chart_description
          }
        : {
            metric_name: newMetric.metric_name,
            chart_type: newMetric.chart_type,
            x_label: newMetric.x_label,
            y_label: newMetric.y_label
          };
      
      await addMetric(selectedProjectMetrics.id, metricData);
      
      loadAdminData(); // Refresh data
      setShowAddMetricForm(false);
      setNewMetric({
        metric_name: '',
        chart_type: 'line',
        x_label: '',
        y_label: '',
        chart_image_url: '',
        chart_description: '',
        chart_file: null
      });
    } catch (error: any) {
      console.error('Error adding metric:', error);
      alert('Failed to add metric: ' + error.message);
    }
  };

  const handleAddMetricData = async (metricId: number) => {
    try {
      await addMetricData(metricId, newMetricData);
      loadAdminData(); // Refresh data
      setShowAddMetricDataForm(false);
      setNewMetricData({
        x_value: '',
        y_value: ''
      });
    } catch (error: any) {
      console.error('Error adding metric data:', error);
      alert('Failed to add metric data: ' + error.message);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-black to-deep-navy p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-deep-navy/80 backdrop-blur-lg rounded-2xl border border-navy-blue p-8 w-full max-w-md"
        >
          <h2 className="text-2xl font-bold text-center mb-6 text-light">Admin Login</h2>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-medium-gray mb-2">Admin Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-dark-navy border border-navy-blue rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent-blue text-light"
                placeholder="Enter admin password"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-navy-blue to-accent-blue hover:from-accent-blue hover:to-navy-blue text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300"
            >
              Login to Admin Panel
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-black to-deep-navy p-4">
      <div className="max-w-7xl mx-auto">
        <header className="py-6 flex justify-between items-center">
          <h1 className="text-3xl font-bold text-light">Admin Dashboard</h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowCalendlyForm(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Manage Calendly Link
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
          <div className="bg-deep-navy/60 p-6 rounded-xl border border-navy-blue">
            <h3 className="text-lg font-semibold text-medium-gray mb-2">Total Projects</h3>
            <p className="text-3xl font-bold text-light">{stats.totalProjects}</p>
          </div>
          <div className="bg-deep-navy/60 p-6 rounded-xl border border-navy-blue">
            <h3 className="text-lg font-semibold text-medium-gray mb-2">Total Leads</h3>
            <p className="text-3xl font-bold text-light">{stats.totalLeads}</p>
          </div>
          <div className="bg-deep-navy/60 p-6 rounded-xl border border-navy-blue">
            <h3 className="text-lg font-semibold text-medium-gray mb-2">Pending Leads</h3>
            <p className="text-3xl font-bold text-accent">{stats.pendingLeads}</p>
          </div>
        </div>

        {/* Calendly Link Management Modal */}
        {showCalendlyForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-deep-navy/90 border border-navy-blue rounded-2xl w-full max-w-2xl">
              <div className="p-6">
                <h3 className="text-xl font-bold text-light mb-4">Manage Calendly Link</h3>
                
                <div className="mb-4">
                  <label className="block text-medium-gray mb-1">Current Calendly Link</label>
                  <input
                    type="text"
                    value={calendlyLink}
                    readOnly
                    className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light cursor-not-allowed"
                  />
                </div>
                
                <div className="mb-6">
                  <label className="block text-medium-gray mb-1">New Calendly Link</label>
                  <input
                    type="text"
                    value={newCalendlyLink}
                    onChange={(e) => setNewCalendlyLink(e.target.value)}
                    placeholder="https://calendly.com/your-profile"
                    className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                  />
                  <p className="text-xs text-medium-gray mt-1">
                    Enter the full Calendly URL (e.g., https://calendly.com/your-profile)
                  </p>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowCalendlyForm(false);
                      setNewCalendlyLink(calendlyLink); // Reset to current value
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateCalendlyLink}
                    className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Update Link
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-deep-navy/60 p-6 rounded-xl border border-navy-blue">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-light">Recent Leads</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-navy-blue">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">Processed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-blue">
                  {leads.slice(0, 5).map((lead: any) => (
                    <tr key={lead.id}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-light">{lead.id}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-light">{lead.name}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-light">{lead.email}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          onClick={() => toggleLeadStatus(lead.id, lead.processed)}
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            lead.processed 
                              ? 'bg-green-800 text-green-200' 
                              : 'bg-yellow-800 text-yellow-200'
                          }`}
                        >
                          {lead.processed ? 'Yes' : 'No'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-deep-navy/60 p-6 rounded-xl border border-navy-blue">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-light">Projects Overview</h2>
              <button
                onClick={() => setShowAddProjectForm(true)}
                className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Add Project
              </button>
            </div>
            
            {showAddProjectForm && (
              <div className="mb-6 bg-navy-blue/30 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-3 text-light">Add New Project</h3>
                <form onSubmit={handleAddProject}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-medium-gray mb-1">Title</label>
                      <input
                        type="text"
                        value={newProject.title}
                        onChange={(e) => setNewProject({...newProject, title: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Category</label>
                      <select
                        value={newProject.category_id}
                        onChange={(e) => setNewProject({...newProject, category_id: Number(e.target.value)})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        required
                      >
                        {categories.map((cat: any) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-medium-gray mb-1">YouTube URL</label>
                      <input
                        type="text"
                        value={newProject.youtube_url}
                        onChange={(e) => setNewProject({...newProject, youtube_url: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Price Start ($)</label>
                      <input
                        type="number"
                        value={newProject.price_start}
                        onChange={(e) => setNewProject({...newProject, price_start: Number(e.target.value)})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">ROI Summary</label>
                      <input
                        type="text"
                        value={newProject.roi_summary}
                        onChange={(e) => setNewProject({...newProject, roi_summary: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Time to Implement</label>
                      <input
                        type="text"
                        value={newProject.time_to_implement}
                        onChange={(e) => setNewProject({...newProject, time_to_implement: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-medium-gray mb-1">Short Description</label>
                      <textarea
                        value={newProject.short_description}
                        onChange={(e) => setNewProject({...newProject, short_description: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        rows={2}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-medium-gray mb-1">Full Description</label>
                      <textarea
                        value={newProject.full_description}
                        onChange={(e) => setNewProject({...newProject, full_description: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        rows={3}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowAddProjectForm(false)}
                      className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Add Project
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
              {projects.map((project: any) => (
                <div key={project.id} className="bg-navy-blue/50 p-4 rounded-lg">
                  <div className="flex justify-between">
                    <h3 className="font-semibold text-light">{project.title}</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setEditingProject({...project})}
                        className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project.id)}
                        className="text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProjectMetrics(project);
                          setShowAddMetricForm(true);
                        }}
                        className="text-xs bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded"
                      >
                        Add Metric
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-light mt-2 line-clamp-2">{project.short_description}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="bg-accent-blue/20 text-accent-blue text-xs px-2 py-1 rounded">
                      Category: {project.category_id}
                    </span>
                    <span className="bg-navy-blue/40 text-light text-xs px-2 py-1 rounded">
                      Price: ${project.price_start}/mo
                    </span>
                  </div>
                  
                  {/* Show project metrics if available */}
                  {project.metrics && project.metrics.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-xs font-semibold text-medium-gray">Metrics:</h4>
                      <ul className="text-xs text-light">
                        {project.metrics.map((metric: any) => (
                          <li key={metric.id} className="ml-2">
                            <div className="font-medium">{metric.metric_name} ({metric.chart_type})</div>
                            <div className="flex flex-wrap gap-2 mt-1">
                              <span className="bg-navy-blue/30 px-2 py-1 rounded text-xs">X-axis: {metric.x_label}</span>
                              <span className="bg-navy-blue/30 px-2 py-1 rounded text-xs">Y-axis: {metric.y_label}</span>
                            </div>
                            
                            {metric.chart_type === 'custom' && metric.chart_image_url && (
                              <div className="mt-2">
                                <img 
                                  src={metric.chart_image_url} 
                                  alt={metric.metric_name} 
                                  className="max-w-xs h-auto rounded border border-navy-blue"
                                />
                                {metric.chart_description && (
                                  <p className="text-xs text-medium-gray mt-1">{metric.chart_description}</p>
                                )}
                              </div>
                            )}
                            
                            <div className="flex space-x-2 mt-2">
                              <button 
                                onClick={() => {
                                  setShowAddMetricDataForm(true);
                                  // Store the metric ID temporarily
                                  (window as any).currentMetricId = metric.id;
                                }}
                                className="text-accent-blue text-xs underline"
                              >
                                Add Data
                              </button>
                              <button 
                                onClick={() => {
                                  // Display detailed view of the metric including X and Y values
                                  let details = `Metric Details:\n\n`;
                                  details += `Name: ${metric.metric_name}\n`;
                                  details += `Type: ${metric.chart_type}\n`;
                                  details += `X-axis: ${metric.x_label}\n`;
                                  details += `Y-axis: ${metric.y_label}\n`;
                                  
                                  if (metric.data && metric.data.length > 0) {
                                    details += `\nData Points (${metric.data.length}):\n`;
                                    metric.data.forEach((dp: any, idx: number) => {
                                      details += `  #${idx + 1}: X=${dp.x}, Y=${dp.y}\n`;
                                    });
                                  } else {
                                    details += '\nNo data points yet.';
                                  }
                                  
                                  if (metric.chart_description) {
                                    details += `\nDescription: ${metric.chart_description}`;
                                  }
                                  
                                  alert(details);
                                }}
                                className="text-yellow-400 text-xs underline"
                              >
                                View Details
                              </button>
                              <button 
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to delete the metric "${metric.metric_name}"? This will also delete all associated data points.`)) {
                                    handleDeleteMetric(metric.id);
                                  }
                                }}
                                className="text-red-400 text-xs underline"
                              >
                                Delete
                              </button>
                            </div>
                            
                            {/* Show data points if available */}
                            {metric.data && metric.data.length > 0 && (
                              <div className="mt-2 ml-4 text-xs text-medium-gray bg-dark-navy/30 p-2 rounded">
                                <details>
                                  <summary className="cursor-pointer hover:text-light">View {metric.data.length} data points</summary>
                                  <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                                    {metric.data.map((dataPoint: any, idx: number) => (
                                      <li key={idx} className="flex justify-between bg-deep-navy/40 p-1 rounded">
                                        <span>X: {dataPoint.x}</span>
                                        <span>Y: {dataPoint.y}</span>
                                        <button 
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (window.confirm('Are you sure you want to delete this data point?')) {
                                              try {
                                                await deleteMetricData(dataPoint.id);
                                                loadAdminData(); // Refresh data
                                              } catch (error: any) {
                                                console.error('Error deleting metric data:', error);
                                                alert('Failed to delete metric data: ' + error.message);
                                              }
                                            }
                                          }}
                                          className="text-red-400 hover:text-red-300"
                                        >
                                          ×
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </details>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Categories Section */}
        <div className="mt-8 bg-deep-navy/60 p-6 rounded-xl border border-navy-blue">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-light">Categories Management</h2>
            <button
              onClick={() => setShowAddCategoryForm(true)}
              className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              Add Category
            </button>
          </div>

          {showAddCategoryForm && (
            <div className="mb-6 bg-navy-blue/30 p-4 rounded-lg">
              <h3 className="font-bold text-lg mb-3 text-light">Add New Category</h3>
              <form onSubmit={handleAddCategory}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-medium-gray mb-1">Category Name</label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                      className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      required
                    />
                  </div>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowAddCategoryForm(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Add Category
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {categories.map((category: any) => (
              <div key={category.id} className="bg-navy-blue/50 p-4 rounded-lg flex justify-between items-center">
                <span className="text-light">{category.name}</span>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-1 rounded"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Add Metric Form */}
        {showAddMetricForm && selectedProjectMetrics && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-deep-navy/90 border border-navy-blue rounded-2xl w-full max-w-2xl">
              <div className="p-6">
                <h3 className="text-xl font-bold text-light mb-4">Add Metric to: {selectedProjectMetrics.title}</h3>
                <form onSubmit={handleAddMetric}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-medium-gray mb-1">Metric Name</label>
                      <input
                        type="text"
                        value={newMetric.metric_name}
                        onChange={(e) => setNewMetric({...newMetric, metric_name: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Chart Type</label>
                      <select
                        value={newMetric.chart_type}
                        onChange={(e) => setNewMetric({...newMetric, chart_type: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      >
                        <option value="line">Line</option>
                        <option value="bar">Bar</option>
                        <option value="area">Area</option>
                        <option value="custom">Custom Chart</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">X-Axis Label</label>
                      <input
                        type="text"
                        value={newMetric.x_label}
                        onChange={(e) => setNewMetric({...newMetric, x_label: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        placeholder={newMetric.chart_type === 'custom' ? "e.g., Time Period, Month, Year" : "e.g., Months, Years, Quarters"}
                        required
                      />
                      <p className="text-xs text-medium-gray mt-1">
                        {newMetric.chart_type === 'custom' 
                          ? "Label for the horizontal axis (independent variable)" 
                          : "What goes on the horizontal axis (independent variable)"}
                      </p>
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Y-Axis Label</label>
                      <input
                        type="text"
                        value={newMetric.y_label}
                        onChange={(e) => setNewMetric({...newMetric, y_label: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        placeholder={newMetric.chart_type === 'custom' ? "e.g., Sales Amount, Users, Growth %" : "e.g., Revenue, Users, Growth %"}
                        required
                      />
                      <p className="text-xs text-medium-gray mt-1">
                        {newMetric.chart_type === 'custom' 
                          ? "Label for the vertical axis (dependent variable)" 
                          : "What goes on the vertical axis (dependent variable)"}
                      </p>
                    </div>
                    
                    {newMetric.chart_type === 'custom' && (
                      <>
                        <div className="md:col-span-2">
                          <label className="block text-medium-gray mb-1">Upload Chart Image</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleChartFileChange}
                            className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                          />
                        </div>
                        
                        {newMetric.chart_image_url && (
                          <div className="md:col-span-2">
                            <label className="block text-medium-gray mb-1">Preview</label>
                            <div className="border border-navy-blue rounded-lg p-2">
                              <img 
                                src={newMetric.chart_image_url} 
                                alt="Chart preview" 
                                className="max-w-full h-auto rounded"
                              />
                            </div>
                          </div>
                        )}
                        
                        <div className="md:col-span-2">
                          <label className="block text-medium-gray mb-1">Chart Description</label>
                          <textarea
                            value={newMetric.chart_description}
                            onChange={(e) => setNewMetric({...newMetric, chart_description: e.target.value})}
                            className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                            placeholder="Explain what this chart represents and any insights..."
                            rows={3}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-6 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddMetricForm(false);
                        setSelectedProjectMetrics(null);
                      }}
                      className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Add Metric
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Add Metric Data Form */}
        {showAddMetricDataForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-deep-navy/90 border border-navy-blue rounded-2xl w-full max-w-2xl">
              <div className="p-6">
                <h3 className="text-xl font-bold text-light mb-4">Add Data Point</h3>
                <p className="text-medium-gray mb-4">Enter values for the X and Y axes to plot on the chart</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-medium-gray mb-1">X Value (Independent Variable)</label>
                    <input
                      type="text"
                      value={newMetricData.x_value}
                      onChange={(e) => setNewMetricData({...newMetricData, x_value: e.target.value})}
                      className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      placeholder="e.g., Month 1, Q1, Jan, 2023"
                    />
                    <p className="text-xs text-medium-gray mt-1">The value along the horizontal axis</p>
                  </div>
                  <div>
                    <label className="block text-medium-gray mb-1">Y Value (Dependent Variable)</label>
                    <input
                      type="text"
                      value={newMetricData.y_value}
                      onChange={(e) => setNewMetricData({...newMetricData, y_value: e.target.value})}
                      className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      placeholder="e.g., 10, 25%, $10,000"
                    />
                    <p className="text-xs text-medium-gray mt-1">The value along the vertical axis</p>
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                  <button
                    onClick={() => setShowAddMetricDataForm(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleAddMetricData((window as any).currentMetricId)}
                    className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Add Data Point
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Editing Project Modal */}
        {editingProject && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-deep-navy/90 border border-navy-blue rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h3 className="text-xl font-bold text-light mb-4">Edit Project: {editingProject.title}</h3>
                <form onSubmit={handleUpdateProject}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-medium-gray mb-1">Title</label>
                      <input
                        type="text"
                        value={editingProject.title}
                        onChange={(e) => setEditingProject({...editingProject, title: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Category</label>
                      <select
                        value={editingProject.category_id}
                        onChange={(e) => setEditingProject({...editingProject, category_id: Number(e.target.value)})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        required
                      >
                        {categories.map((cat: any) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-medium-gray mb-1">YouTube URL</label>
                      <input
                        type="text"
                        value={editingProject.youtube_url}
                        onChange={(e) => setEditingProject({...editingProject, youtube_url: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Price Start ($)</label>
                      <input
                        type="number"
                        value={editingProject.price_start}
                        onChange={(e) => setEditingProject({...editingProject, price_start: Number(e.target.value)})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">ROI Summary</label>
                      <input
                        type="text"
                        value={editingProject.roi_summary}
                        onChange={(e) => setEditingProject({...editingProject, roi_summary: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      />
                    </div>
                    <div>
                      <label className="block text-medium-gray mb-1">Time to Implement</label>
                      <input
                        type="text"
                        value={editingProject.time_to_implement}
                        onChange={(e) => setEditingProject({...editingProject, time_to_implement: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-medium-gray mb-1">Short Description</label>
                      <textarea
                        value={editingProject.short_description}
                        onChange={(e) => setEditingProject({...editingProject, short_description: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        rows={2}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-medium-gray mb-1">Full Description</label>
                      <textarea
                        value={editingProject.full_description}
                        onChange={(e) => setEditingProject({...editingProject, full_description: e.target.value})}
                        className="w-full bg-dark-navy border border-navy-blue rounded-lg px-3 py-2 text-light"
                        rows={3}
                      />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setEditingProject(null)}
                      className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-accent-blue hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Update Project
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;